import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api/axios";

/* ─── Brand palette ──────────────────────────────────────────── */
const BRAND = {
  forest: "#0E3A2C", pine: "#1A5440", gold: "#C9952A", honey: "#E8C96A",
  cream: "#FDF6E3", sand: "#F1E6C9", ink: "#15281D", stone: "#4C5C46",
};

/* open gold · in_progress honey · resolved pine · closed stone */
export const STATUS_CHIP = {
  open:        { label: "Open",        bg: BRAND.cream,  color: BRAND.gold,  border: BRAND.honey },
  in_progress: { label: "In progress", bg: "#FBF0D2",    color: "#A97B1F",   border: BRAND.honey },
  resolved:    { label: "Resolved",    bg: "#E4F0EA",    color: BRAND.pine,  border: "#9CC7B4" },
  closed:      { label: "Closed",      bg: "#EFF1ED",    color: BRAND.stone, border: "#C9D1C4" },
};

export const CATEGORY_CHIP = {
  quality:    { label: "Quality",       bg: BRAND.cream, color: BRAND.gold,   border: BRAND.honey },
  wrong_item: { label: "Wrong item",    bg: "#FDECEA",   color: "#C62828",    border: "#F5B7B1" },
  delivery:   { label: "Delivery",      bg: "#E4F0EA",   color: BRAND.pine,   border: "#9CC7B4" },
  payment:    { label: "Payment",       bg: "#EAF1FB",   color: "#1D5FA8",    border: "#AECBEB" },
  other:      { label: "Other",         bg: "#EFF1ED",   color: BRAND.stone,  border: "#C9D1C4" },
};

/* item-level issue chips — wrong=gold · missing=honey · quality=red · other=stone */
export const ISSUE_CHIP = {
  wrong:   { label: "Wrong item", bg: BRAND.cream, color: BRAND.gold,  border: BRAND.gold },
  missing: { label: "Missing",    bg: BRAND.honey, color: BRAND.ink,   border: "#D6B44E" },
  quality: { label: "Quality",    bg: "#FDECEA",   color: "#C62828",   border: "#F5B7B1" },
  other:   { label: "Other",      bg: "#EFF1ED",   color: BRAND.stone, border: "#C9D1C4" },
};

export const Chip = ({ spec, text }) => (
  <span style={{
    background: spec.bg, color: spec.color, border: `1px solid ${spec.border}`,
    fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20,
    whiteSpace: "nowrap", display: "inline-block",
  }}>
    {text || spec.label}
  </span>
);

/* ─── Field readers ───────────────────────────────────────────────────────
   GET /admin/support/tickets serializes camelCase, and the ticket id is a
   NUMBER (bigserial) rather than a Mongo _id — /admin/support/{id:long}
   takes exactly that number. ── */

export const ticketIdOf   = (t) => (t?.id === undefined || t?.id === null ? "" : String(t.id));
export const ticketNoOf   = (t) => t?.ticketNo || "";
export const orderNumberOf = (t) => t?.orderNo || "";
export const updatedAtOf  = (t) => t?.updatedAt || t?.createdAt || null;

export const orderIdOf = (t) => {
  const o = t?.orderId;
  return o === undefined || o === null ? null : o;
};

export const ticketItemsOf = (t) => (Array.isArray(t?.items) ? t.items : []);

const ISSUE_ORDER = ["wrong", "missing", "quality", "other"];

export const issueOf = (it) => {
  const raw = String(it?.issue || it?.issue_type || "other").trim().toLowerCase();
  return ISSUE_ORDER.includes(raw) ? raw : "other";
};

export const issueSpec = (issue) => ISSUE_CHIP[issueOf({ issue })] || ISSUE_CHIP.other;

/* "2 wrong, 1 missing" — client fallback when the row carries no summary */
export const summarizeIssues = (items) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return "";
  const counts = {};
  list.forEach((it) => {
    const k = issueOf(it);
    counts[k] = (counts[k] || 0) + 1;
  });
  return ISSUE_ORDER.filter((k) => counts[k]).map((k) => `${counts[k]} ${k}`).join(", ");
};

export const itemsCountOf = (t) => {
  const raw = t?.items_count !== undefined && t?.items_count !== null ? t.items_count : t?.itemsCount;
  const n = Number(raw);
  if (raw !== undefined && raw !== null && Number.isFinite(n)) return Math.max(0, Math.floor(n));
  return ticketItemsOf(t).length;
};

export const issueSummaryOf = (t) => {
  const given = t?.issue_summary || t?.issueSummary;
  if (given && String(given).trim()) return String(given).trim();
  return summarizeIssues(ticketItemsOf(t));
};

/* ─── Filters ─────────────────────────────────────────────────
   `statuses: null` means "everything" (no ?status sent).       */
const FILTERS = [
  { key: "pending",     label: "Pending",     statuses: ["open", "in_progress"] },
  { key: "open",        label: "Open",        statuses: ["open"] },
  { key: "in_progress", label: "In progress", statuses: ["in_progress"] },
  { key: "resolved",    label: "Resolved",    statuses: ["resolved"] },
  { key: "closed",      label: "Closed",      statuses: ["closed"] },
  { key: "",            label: "All",         statuses: null },
];

const DEFAULT_FILTER = "pending";

const filterSpec = (key) =>
  FILTERS.find((f) => f.key === key) || FILTERS.find((f) => f.key === DEFAULT_FILTER);

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
};

const POLL_MS = 15000;

const listOf = (raw) =>
  Array.isArray(raw) ? raw
    : Array.isArray(raw?.data) ? raw.data
    : Array.isArray(raw?.tickets) ? raw.tickets
    : Array.isArray(raw?.data?.tickets) ? raw.data.tickets
    : [];

/* errors are a real HTTP status plus {code, message} — never a status field
   inside a 200 body, so the status is what decides what to say */
const loadErrorMsg = (err) => {
  if (err?.response?.status === 403)
    return "This account cannot read the support board — its token carries no store.";
  return err?.response?.data?.message || "Failed to load tickets";
};

const sortByUpdated = (rows) =>
  [...rows].sort((a, b) => {
    const ta = new Date(updatedAtOf(a) || 0).getTime() || 0;
    const tb = new Date(updatedAtOf(b) || 0).getTime() || 0;
    return tb - ta;
  });

const SupportList = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [filter,  setFilter]  = useState(DEFAULT_FILTER);

  /* ids of OPEN tickets already seen — used to toast only on NEW open tickets */
  const seenOpenIds = useRef(null); // null = first load not done yet
  const filterRef   = useRef(DEFAULT_FILTER);
  const ticketsRef  = useRef([]);
  const loadedAll   = useRef(false); // first load pulls everything so the chip counts are real
  const serverFilter = useRef(true); // flipped off if the API rejects ?status= lists
  const didMount    = useRef(false);

  const applyTickets = useCallback((rows) => {
    const next = sortByUpdated(rows);
    ticketsRef.current = next;
    setTickets(next);
  }, []);

  /* limit is explicit: the server defaults to 50 and clamps at 200, and the
     unfiltered load feeds the chip counts — a default page would undercount */
  const loadList = useCallback(async (statusParam) => {
    const params = { limit: 200 };
    if (statusParam) params.status = statusParam;
    const res = await api.get("/admin/support/tickets", { params });
    return listOf(res.data);
  }, []);

  const fetchTickets = useCallback(async (silent = false) => {
    const spec = filterSpec(filterRef.current);
    /* the first load is always unfiltered: chip counts need the whole board */
    const useFilter = loadedAll.current && serverFilter.current && Array.isArray(spec.statuses);
    const param = useFilter ? spec.statuses.join(",") : "";

    let rows = null;
    let covered = useFilter ? spec.statuses : null;

    try {
      rows = await loadList(param);
    } catch (err) {
      /* the server refuses an unknown ?status value outright (400
         invalid_status) — retry unfiltered rather than blanking the table */
      if (param) {
        /* 400 = this server rejected the ?status list — stop sending it for
           the rest of the session instead of retrying on every poll; the
           client-side filter covers the same ground */
        if (err.response?.status === 400) serverFilter.current = false;
        try {
          rows = await loadList("");
          covered = null;
        } catch (err2) {
          if (!silent) setError(loadErrorMsg(err2));
          setLoading(false);
          return;
        }
      } else {
        if (!silent) setError(loadErrorMsg(err));
        setLoading(false);
        return;
      }
    }

    if (!covered) {
      loadedAll.current = true;
      applyTickets(rows);
    } else {
      /* merge a filtered page into the cache: rows inside the covered
         statuses are authoritative, everything else keeps its last value */
      const fresh = new Set(rows.map((t) => ticketIdOf(t)));
      const prev = ticketsRef.current;
      let dropped = false;
      const kept = prev.filter((t) => {
        if (fresh.has(ticketIdOf(t))) return false;
        if (covered.includes(t.status)) { dropped = true; return false; }
        return true;
      });
      applyTickets([...rows, ...kept]);
      /* a row leaving the filter (resolved elsewhere) means the other chips
         are now stale — resync the full board once, quietly */
      if (dropped) {
        try {
          const all = await loadList("");
          applyTickets(all);
        } catch (e) { /* resync is best-effort — keep the merged view */ }
      }
    }

    setError("");

    const openNow = ticketsRef.current.filter((t) => t.status === "open");
    if (seenOpenIds.current === null) {
      seenOpenIds.current = new Set(openNow.map((t) => ticketIdOf(t)));
    } else {
      openNow
        .filter((t) => !seenOpenIds.current.has(ticketIdOf(t)))
        .forEach((t) => {
          seenOpenIds.current.add(ticketIdOf(t));
          toast.info(`New support ticket ${ticketNoOf(t)} — ${t.subject || "no subject"}`);
        });
    }

    setLoading(false);
  }, [applyTickets, loadList]);

  useEffect(() => {
    fetchTickets();
    const id = setInterval(() => fetchTickets(true), POLL_MS);
    return () => clearInterval(id);
  }, [fetchTickets]);

  /* switching chips refetches with that chip's ?status= */
  useEffect(() => {
    filterRef.current = filter;
    if (!didMount.current) { didMount.current = true; return; }
    fetchTickets(true);
  }, [filter, fetchTickets]);

  const matches = (t, key) => {
    const spec = filterSpec(key);
    if (!spec.statuses) return true;
    return spec.statuses.includes(t.status);
  };

  const countFor = (key) => (key === "" ? tickets.length : tickets.filter((t) => matches(t, key)).length);

  const filtered = tickets.filter((t) => matches(t, filter));

  /* the row carries the customer flattened: customerName / customerPhone */
  const customerLabel = (t) => t.customerName || t.customerPhone || "—";

  const openWithAction = (e, t, action) => {
    e.stopPropagation();
    navigate(`/admin/support/${ticketIdOf(t)}`, { state: { action } });
  };

  const quickBtn = (bg, border, color) => ({
    padding: "5px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700,
    border: `1.5px solid ${border}`, background: bg, color,
    cursor: "pointer", whiteSpace: "nowrap",
  });

  const emptyHint = filter
    ? `No ${(filterSpec(filter).label || "").toLowerCase()} tickets right now`
    : "No support tickets yet";

  return (
    <div>
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ flexWrap: "wrap", gap: 10 }}>
            <h5 style={{ margin: 0 }}>Support Tickets</h5>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {FILTERS.map((f) => {
                const active = filter === f.key;
                return (
                  <button
                    key={f.key || "all"}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    style={{
                      border: `1.5px solid ${active ? "var(--mly-heading)" : "var(--mly-border)"}`,
                      background: active ? "var(--mly-heading)" : "var(--mly-surface)",
                      color: active ? "var(--mly-on-accent)" : "var(--mly-text-soft)",
                      borderRadius: 999, padding: "6px 14px", fontSize: 13,
                      fontWeight: 600, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 6,
                    }}
                  >
                    {f.label}
                    <span style={{
                      background: active ? BRAND.gold : "var(--mly-surface-3)",
                      color: active ? "#fff" : "var(--mly-text-muted)",
                      borderRadius: 999, minWidth: 20, height: 20,
                      fontSize: 11, lineHeight: "20px", textAlign: "center",
                      fontWeight: 700, padding: "0 5px",
                    }}>
                      {countFor(f.key)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : error ? (
              <div className="text-danger text-center py-4">{error}</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="display table table-bordered" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>Category</th>
                      <th>Subject</th>
                      <th>Items</th>
                      <th>Customer</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Updated</th>
                      <th>Quick actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ textAlign: "center", padding: "40px 0" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
                              fill="none" stroke="var(--mly-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                              <circle cx="12" cy="12" r="4" />
                              <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
                              <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
                              <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
                              <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
                            </svg>
                            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--mly-text-muted)" }}>No tickets here</span>
                            <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>{emptyHint}</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filtered.map((t, rowIdx) => {
                        const st  = STATUS_CHIP[t.status]  || STATUS_CHIP.open;
                        const cat = CATEGORY_CHIP[t.category] || CATEGORY_CHIP.other;
                        const oid = orderIdOf(t);
                        const tid = ticketIdOf(t);
                        const nItems  = itemsCountOf(t);
                        const summary = issueSummaryOf(t);
                        const isPending = t.status === "open" || t.status === "in_progress";
                        return (
                          <tr
                            key={tid || `row-${rowIdx}`}
                            onClick={() => navigate(`/admin/support/${tid}`)}
                            style={{ cursor: "pointer" }}
                          >
                            <td style={{ fontWeight: 700, whiteSpace: "nowrap", color: "var(--mly-heading)" }}>
                              {ticketNoOf(t) || "—"}
                            </td>
                            <td><Chip spec={cat} /></td>
                            <td style={{ maxWidth: 260 }}>
                              <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {t.subject || "—"}
                              </div>
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {nItems > 0 ? (
                                <>
                                  <span style={{
                                    background: BRAND.cream, color: BRAND.gold,
                                    border: `1px solid ${BRAND.honey}`, borderRadius: 20,
                                    fontSize: 11, fontWeight: 700, padding: "3px 10px",
                                    display: "inline-block",
                                  }}>
                                    {nItems} item{nItems === 1 ? "" : "s"}
                                  </span>
                                  {summary && (
                                    <div style={{ fontSize: 11, color: "var(--mly-text-muted)", marginTop: 3 }}>{summary}</div>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: "var(--mly-text-muted)" }}>—</span>
                              )}
                            </td>
                            <td>{customerLabel(t)}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              {oid ? (
                                <Link
                                  to={`/admin/orders/details/${oid}`}
                                  style={{ color: "var(--mly-heading)", fontWeight: 600, textDecoration: "underline", whiteSpace: "nowrap" }}
                                >
                                  {orderNumberOf(t) || "View order"}
                                </Link>
                              ) : (
                                <span style={{ color: "var(--mly-text-muted)" }}>—</span>
                              )}
                            </td>
                            <td><Chip spec={st} /></td>
                            <td style={{ whiteSpace: "nowrap", fontSize: 12, color: "var(--mly-text-muted)" }}>
                              {formatDateTime(updatedAtOf(t))}
                            </td>
                            <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                              {isPending ? (
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                  <button
                                    type="button"
                                    title="Open the ticket with the replacement panel ready"
                                    onClick={(e) => openWithAction(e, t, "replacement")}
                                    style={quickBtn("var(--mly-heading)", "var(--mly-heading)", "var(--mly-on-accent)")}
                                  >
                                    Replace
                                  </button>
                                  <button
                                    type="button"
                                    title="Open the ticket with the missing-items panel ready"
                                    onClick={(e) => openWithAction(e, t, "missing")}
                                    style={quickBtn("var(--mly-surface-2)", BRAND.honey, "var(--mly-accent-2)")}
                                  >
                                    Send missing
                                  </button>
                                </div>
                              ) : (
                                <span style={{ color: "var(--mly-text-muted)" }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportList;
